import { concat, get, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismIconBrand,
  PrismLoadingBar,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import type { Ota } from 'frontend-utils/models';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import getNumberSignClass from 'lumen/helpers/get-number-sign-class';

interface HotelOverviewDashboardTilesDistributionHealthRankingAndParityTileSignature {
  Args: {
    allParityData: unknown;
    allRankingData: unknown;
    compsetId: unknown;
    isLoading: unknown;
    otaId: unknown;
    rateParam: unknown;
    selectedRange: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesDistributionHealthRankingAndParityTile extends Component<HotelOverviewDashboardTilesDistributionHealthRankingAndParityTileSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare store: Services['store'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOtaName() {
    // @ts-expect-error: Incorrect type
    return this.ota.otaIdToPrettyName(this.args.otaId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPriceIssueAverage() {
    let result = this.intl.t('general.missing');

    if (this.parityDataForOwnTile.priceIssueAverage !== null) {
      // @ts-expect-error: Incorrect type
      result = this.hotelAndMarketService.formatRate(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.parityDataForOwnTile.priceIssueAverage,
        true,
        true,
        { maximumFractionDigits: 0, minimumFractionDigits: 0 },
        true,
      );
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasParityData() {
    return (
      this.args.allParityData &&
      // @ts-expect-error: Incorrect type
      Object.keys(this.args.allParityData).includes(this.args.otaId)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRankingData() {
    return (
      this.args.allRankingData &&
      // @ts-expect-error: Incorrect type
      Object.keys(this.args.allRankingData).includes(this.args.otaId)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaRankingData() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      own:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.rankingDataForOwnTile['ranking.own.current'] ??
        this.intl.t('general.missing'),
      compsetPosition: this.rankingDataForOwnTile[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        'ranking.own.compset_position'
      ]
        ? this.formatOrdinal(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            this.rankingDataForOwnTile['ranking.own.compset_position'],
          )
        : this.intl.t('general.missing'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      ownWow: this.rankingDataForOwnTile['ranking.own.wow'] ?? null,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      propertiesCount:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.rankingDataForOwnTile['ranking.total.count'] ??
        this.intl.t('general.missing'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityDataForOwnTile() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      issueCount:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.allParityData[this.args.otaId].issue_count ??
        this.intl.t('general.missing'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      priceIssueAverage:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.allParityData[this.args.otaId].price_issue_average,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rankingDataForOwnTile() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.allRankingData?.[this.args.otaId] || {};
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get reviewScores() {
    // @ts-expect-error: Incorrect type
    const ota = this.store.peekRecord<Ota>('ota', this.args.otaId);

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      own:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.rankingDataForOwnTile['review_score.own.current'] ??
        this.intl.t('general.missing'),
      compsetPosition: this.rankingDataForOwnTile[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        'review_score.own.compset_position'
      ]
        ? this.formatOrdinal(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            this.rankingDataForOwnTile['review_score.own.compset_position'],
          )
        : this.intl.t('general.missing'),
      maxReviewScore: ota?.maxReviewScore ?? this.intl.t('general.missing'),
    };
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatOrdinal(value) {
    if (value === null || value === undefined) {
      return this.intl.t('general.missing');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const number = this.intl.formatNumber(value);
    const locale = this.intl.primaryLocale;

    const pluralRule = new Intl.PluralRules('en', { type: 'ordinal' }).select(
      // @ts-expect-error: Incorrect type
      number,
    );

    switch (locale) {
      case 'en':
      case 'en-us':
      case 'en-gb': {
        const ordinals = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
        // @ts-expect-error: Incorrect type
        return `${number}${ordinals[pluralRule]}`;
      }
      case 'de':
      case 'ja':
      case 'zh-tw':
        return `${number}.`;
      case 'nl-nl': {
        // @ts-expect-error: Incorrect type
        const remainder = number % 100;
        // @ts-expect-error: Incorrect type
        return (number !== 0 && remainder <= 1) ||
          remainder === 8 ||
          remainder >= 20
          ? `${number}ste`
          : `${number}de`;
      }
      case 'fr':
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return number === 1 ? `${number}er` : `${number}e`;
      case 'pt-br':
      case 'it':
        return `${number}º`;
      case 'es': {
        // @ts-expect-error: Incorrect type
        const b = number % 10;
        return b === 1 || b === 3
          ? `${number}er`
          : b === 2
            ? `${number}do`
            : b === 7 || b === 0
              ? `${number}mo`
              : b === 8
                ? `${number}vo`
                : b === 9
                  ? `${number}no`
                  : `${number}to`;
      }
      default:
        return number;
    }
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard @isParent={{true}} class="prism-card--od-ranking">
      <:header as |Header|>
        <div class="flex items-center gap-200">
          <PrismIconBrand @name={{@otaId}} />
          <Header.title>
            {{this.formattedOtaName}}
          </Header.title>
        </div>
      </:header>
      <:content>
        <div class="od-ranking-content">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}
            {{#if this.hasRankingData}}
              <PrismCard @isSecondary={{true}}>
                <:header as |Header|>
                  <Header.title>
                    {{t "ranking.ranking"}}
                  </Header.title>
                </:header>

                <:headerAfter>
                  <LinkTo
                    @query={{hash
                      compsetId=@compsetId
                      otaId=@otaId
                      timespan="weekly"
                    }}
                    @route="hotel.ranking"
                    class="prism-link text-200--medium"
                    data-event-id="hotel_overview-kpi_main_redirect-click"
                    data-event-value={{concat @tileId "_ranking"}}
                    target="_blank"
                    {{! extra addition to be able to distinguish between the multiple redirect links in the KPI }}
                  >
                    {{t "overview_dashboard.kpi.sub_item.link"}}
                  </LinkTo>
                </:headerAfter>
                <:content>
                  <div
                    class="od-ranking-metric-blocks-wrapper mt-300 flex flex-row justify-around"
                  >
                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span class="text-400--bold">
                            {{this.otaRankingData.own}}

                            {{#if this.otaRankingData.ownWow}}
                              <span
                                class="text-200--regular
                                  {{getNumberSignClass
                                    this.otaRankingData.ownWow
                                  }}"
                              >
                                {{formatNumber
                                  this.otaRankingData.ownWow
                                  signDisplay="always"
                                }}
                              </span>
                            {{/if}}
                          </span>
                        </:value>
                        <:label>
                          {{t
                            "overview_dashboard.kpi.sub_item.out_of_all"
                            numberOfProperties=this.otaRankingData.propertiesCount
                          }}
                        </:label>
                      </PrismMetricBlock>
                    </div>

                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span class="text-400--bold">
                            {{this.otaRankingData.compsetPosition}}
                          </span>
                        </:value>
                        <:label>
                          {{t "overview_dashboard.kpi.sub_item.compset"}}
                        </:label>
                      </PrismMetricBlock>
                    </div>
                  </div>
                </:content>
              </PrismCard>

              <PrismCard @isSecondary={{true}}>
                <:header as |Header|>
                  <Header.title>
                    {{t "ranking.review_score"}}
                  </Header.title>
                </:header>

                <:headerAfter>
                  <LinkTo
                    @query={{hash
                      compsetId=@compsetId
                      otaId=@otaId
                      timespan="weekly"
                    }}
                    @route="hotel.ranking"
                    class="prism-link text-200--medium"
                    data-event-id="hotel_overview-kpi_main_redirect-click"
                    data-event-value={{concat @tileId "_review"}}
                    target="_blank"
                    {{! extra addition to be able to distinguish between the multiple redirect links in the KPI }}
                  >
                    {{t "overview_dashboard.kpi.sub_item.link"}}
                  </LinkTo>
                </:headerAfter>

                <:content>
                  <div
                    class="od-ranking-metric-blocks-wrapper mt-300 flex flex-row justify-around"
                  >
                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span class="text-400--bold">
                            {{this.reviewScores.own}}
                            /
                            {{this.reviewScores.maxReviewScore}}
                          </span>
                        </:value>
                        <:label>
                          {{t "general.own_hotel"}}
                        </:label>
                      </PrismMetricBlock>
                    </div>

                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span class="text-400--bold">
                            {{this.reviewScores.compsetPosition}}
                          </span>
                        </:value>
                        <:label>
                          {{t "overview_dashboard.kpi.sub_item.compset"}}
                        </:label>
                      </PrismMetricBlock>
                    </div>
                  </div>
                </:content>
              </PrismCard>
            {{/if}}

            {{#if this.hasParityData}}
              <PrismCard @isSecondary={{true}}>
                <:header as |Header|>
                  <Header.title>
                    {{t
                      "overview_dashboard.kpi.sub_header.parity_issues_next_90_days"
                    }}
                  </Header.title>
                </:header>

                <:headerAfter>
                  <LinkTo
                    @query={{hash
                      los=(get
                        this.hotelAndMarketService.hotelOrMarket.defaultLosRates
                        "0"
                      )
                      maxPersons=(this.hotelAndMarketService.hotelOrMarket.defaultPersons
                      )
                      month=(dayjsFormat @selectedRange.start format="YYYY-MM")
                      rateParam=@rateParam
                    }}
                    @route="hotel.parity"
                    class="prism-link text-200--medium"
                    data-event-id="hotel_overview-kpi_main_redirect-click"
                    data-event-value={{concat @tileId "_parity"}}
                    target="_blank"
                    {{! extra addition to be able to distinguish between the multiple redirect links in the KPI }}
                  >
                    {{t "overview_dashboard.kpi.sub_item.link"}}
                  </LinkTo>
                </:headerAfter>

                <:content>
                  <div
                    class="od-ranking-metric-blocks-wrapper mt-300 flex flex-row justify-around"
                  >
                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span class="text-400--bold">
                            {{this.parityDataForOwnTile.issueCount}}
                          </span>
                        </:value>
                        <:label>
                          {{t
                            "general.issues"
                            count=this.parityDataForOwnTile.issueCount
                          }}
                        </:label>
                      </PrismMetricBlock>
                    </div>

                    <div class="od-ranking-metric-block">
                      <PrismMetricBlock>
                        <:value>
                          <span
                            class="text-400--bold
                              {{getNumberSignClass
                                this.parityDataForOwnTile.priceIssueAverage
                              }}"
                          >
                            {{this.formattedPriceIssueAverage}}
                          </span>
                        </:value>
                        <:label>
                          {{t "overview_dashboard.kpi.sub_header.parity"}}
                        </:label>
                      </PrismMetricBlock>
                    </div>
                  </div>
                </:content>
              </PrismCard>
            {{/if}}
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
